import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Notify } from "@/components/ui/notify";
import { Pill } from "@/components/ui/pill";
import type { BatchCharacteristicResponse } from "@/types/batch-characteristics.types";

interface CharacteristicListProps {
  items: BatchCharacteristicResponse[];
  canEdit: boolean;
  onEdit: (item: BatchCharacteristicResponse) => void;
  onDelete: (item: BatchCharacteristicResponse) => void;
  onNew: () => void;
}

export function CharacteristicList({
  items,
  canEdit,
  onEdit,
  onDelete,
  onNew,
}: CharacteristicListProps) {
  if (items.length === 0) {
    return (
      <Notify.Empty
        title="Nenhuma característica de batelada"
        description="Declare o que uma batelada carrega desde a entrada (um tipo de matéria-prima, um lote de fornecedor) e toda saída dela, também nas etapas seguintes, já chega marcada."
        action={
          canEdit ? (
            <Button size="sm" onClick={onNew}>
              Declarar característica
            </Button>
          ) : undefined
        }
      />
    );
  }

  return (
    <ul className="divide-y divide-border-default rounded-md border border-border-default">
      {items.map((c) => (
        <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
          <div className="min-w-0 space-y-0.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[14px] font-medium text-text-primary">{c.name}</span>
              {c.required ? <Pill tone="info">Obrigatória</Pill> : <Pill>Opcional</Pill>}
            </div>
            <div className="text-[12px] text-text-secondary">
              {c.kind === "LIST"
                ? `Lista: ${c.values.map((v) => v.label).join(", ")}`
                : "Texto livre"}
              {" · "}
              {c.lines.length === 0
                ? "sem linha de marcação (sem efeito)"
                : `marcada em ${c.lines.map((l) => l.name).join(", ")}`}
            </div>
          </div>
          {canEdit ? (
            <div className="flex gap-1.5">
              <Button
                variant="outline"
                size="sm"
                aria-label={`Editar ${c.name}`}
                onClick={() => onEdit(c)}
              >
                <Pencil size={14} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                aria-label={`Apagar ${c.name}`}
                onClick={() => onDelete(c)}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
